// frontend/src/pages/Admin/SellersManagement.jsx
// ✅ GESTIÓN DE VENDEDORES (USUARIOS)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardBody,
  Table,
  Badge,
  Button,
  Input,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Alert,
} from 'reactstrap';
import { usersAPI } from '../../services/api';
import { parseDate } from '../../utils/dates';

function SellersManagement() {
  const navigate = useNavigate();
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSeller, setEditingSeller] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
  });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [stats, setStats] = useState({});

  useEffect(() => {
    loadSellers();
  }, []);

  const loadSellers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getAll();
      // Filtrar solo usuarios que NO son super_admin (todos los demás son vendedores)
      const sellersData = (response.data.users || []).filter(
        u => u.role !== 'super_admin' && u.is_approved
      );
      setSellers(sellersData);
      
      // Cargar estadísticas (esto lo implementaremos en el backend después)
      // Por ahora, stats vacías
      setStats({});
    } catch (error) {
      console.error('Error cargando vendedores:', error);
      alert('Error al cargar vendedores');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (seller) => {
    setEditingSeller(seller);
    setFormData({
      first_name: seller.first_name || '',
      last_name: seller.last_name || '',
      phone: seller.phone || '',
    });
    setError(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSeller(null);
    setFormData({
      first_name: '',
      last_name: '',
      phone: '',
    });
    setError(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.first_name || !formData.last_name) {
      setError('Nombre y apellido son obligatorios');
      return;
    }

    setSubmitting(true);

    try {
      await usersAPI.update(editingSeller.id, formData);
      alert('Vendedor actualizado exitosamente');
      handleCloseModal();
      loadSellers();
    } catch (error) {
      console.error('Error actualizando vendedor:', error);
      setError(error.response?.data?.error || 'Error al actualizar vendedor');
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ LÍNEA 112 CORREGIDO:
  const filteredSellers = sellers.filter(s => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (s.full_name && s.full_name.toLowerCase().includes(searchLower)) ||
      (s.email && s.email.toLowerCase().includes(searchLower)) ||
      (s.phone && s.phone.toLowerCase().includes(searchLower))
    );
  });


  const activeSellers = sellers.filter(s => s.is_approved).length;

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="text-muted mt-3">Cargando vendedores...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header con estadísticas */}
      <Row className="mb-4">
        <Col lg="12">
          <Card className="shadow border-0">
            <CardBody className="p-3">
              <Row className="align-items-center">
                <Col lg="8">
                  <div className="d-flex align-items-center">
                    <div className="icon icon-shape bg-gradient-primary shadow text-center rounded-circle me-3">
                      <i className="ni ni-badge text-white text-lg opacity-10"></i>
                    </div>
                    <div>
                      <h5 className="mb-0">Gestión de Vendedores</h5>
                      <p className="text-sm mb-0 text-secondary">
                        Administra los vendedores y sus perfiles
                      </p>
                    </div>
                  </div>
                </Col>
                <Col lg="4" className="text-end">
                  <Badge color="success" pill className="badge-lg">
                    <i className="ni ni-check-bold me-1"></i>
                    {activeSellers} vendedores activos
                  </Badge>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Alerta informativa */}
      <Row className="mb-4">
        <Col lg="12">
          
        </Col>
      </Row>

      {/* Tabla de vendedores */}
      <Row>
        <Col lg="12">
          <Card className="shadow border-0">
            <CardHeader className="bg-transparent border-0">
              <Row className="align-items-center">
                <Col lg="6">
                  <h6 className="mb-0">
                    <i className="ni ni-bullet-list-67 me-2"></i>
                    Vendedores ({filteredSellers.length})
                  </h6>
                </Col>
                <Col lg="6">
                  <Input
                    type="text"
                    className="form-control-sm"
                    placeholder="🔍 Buscar vendedor..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Col>
              </Row>
            </CardHeader>

            <CardBody className="px-0 pt-0 pb-2">
              <div className="table-responsive p-0">
                <Table className="align-items-center mb-0">
                  <thead>
                    <tr>
                      <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Vendedor
                      </th>
                      <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                        Email
                      </th>
                      <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                        Teléfono
                      </th>
                      <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Rol
                      </th>
                      <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Estadísticas
                      </th>
                      <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSellers.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          <div className="py-4">
                            <i className="ni ni-badge text-muted" style={{ fontSize: '3rem' }}></i>
                            <p className="text-secondary mb-0 mt-2">
                              {searchTerm 
                                ? 'No se encontraron vendedores con ese criterio' 
                                : 'No hay vendedores registrados'}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredSellers.map((seller) => (
                        <tr 
                          key={seller.id}
                          style={{ cursor: 'pointer' }}  // ✅ NUEVO
                          onClick={() => navigate(`/admin/sellers/${seller.id}`)}  // ✅ NUEVO
                        >
                          <td>
                            <div className="d-flex px-3 py-1">
                              {seller.profile_picture ? (
                                <img
                                  src={seller.profile_picture}
                                  className="avatar avatar-sm me-3 shadow"
                                  alt={seller.full_name}
                                />
                              ) : (
                                <div className="avatar avatar-sm bg-gradient-primary me-3 shadow">
                                  <span className="text-white text-xs font-weight-bold">
                                    {seller.first_name?.[0]}{seller.last_name?.[0]}
                                  </span>
                                </div>
                              )}
                              <div className="d-flex flex-column justify-content-center">
                                <h6 className="mb-0 text-sm">{seller.full_name}</h6>
                                <p className="text-xs text-secondary mb-0">
                                  <i className="ni ni-calendar-grid-58 me-1"></i>
                                  Desde {parseDate(seller.created_at).toLocaleDateString('es-MX')}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td>
                            <p className="text-xs font-weight-bold mb-0">
                              <i className="ni ni-email-83 me-1"></i>
                              {seller.email}
                            </p>
                          </td>
                          <td>
                            <p className="text-xs font-weight-bold mb-0">
                              <i className="ni ni-mobile-button me-1"></i>
                              {seller.phone || 'N/A'}
                            </p>
                          </td>
                          <td className="align-middle text-center">
                            <Badge color="primary" className="badge-sm">
                              {seller.role_display || seller.role}
                            </Badge>
                          </td>
                          <td className="align-middle text-center">
                            <div className="d-flex flex-column align-items-center">
                              <small className="text-xs text-muted mb-1">
                                <i className="ni ni-single-copy-04 me-1"></i>
                                Cotizaciones: <strong>{stats[seller.id]?.quotes || 0}</strong>
                              </small>
                              <small className="text-xs text-muted">
                                <i className="ni ni-bag-17 me-1"></i>
                                Órdenes: <strong>{stats[seller.id]?.orders || 0}</strong>
                              </small>
                            </div>
                          </td>
                          <td className="align-middle text-center">
                            <Button
                              color="info"
                              size="sm"
                              onClick={(e) => {  // ✅ CAMBIO: agregar e.stopPropagation()
                                e.stopPropagation();
                                handleOpenModal(seller);
                              }}
                            >
                              <i className="ni ni-settings-gear-65 me-1"></i>
                              Editar Perfil
                            </Button>
                          </td>

                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Modal para editar perfil */}
      <Modal isOpen={showModal} toggle={handleCloseModal} centered>
        <ModalHeader toggle={handleCloseModal}>
          <i className="ni ni-badge me-2"></i>
          Editar Perfil de Vendedor
        </ModalHeader>
        <form onSubmit={handleSubmit}>
          <ModalBody>
            {error && (
              <Alert color="danger">
                <span className="alert-icon"><i className="ni ni-support-16"></i></span>
                <span className="alert-text">{error}</span>
              </Alert>
            )}

            {editingSeller && (
              <Alert color="info" className="mb-3">
                <span className="alert-icon"><i className="ni ni-email-83"></i></span>
                <span className="alert-text">
                  <strong>Email:</strong> {editingSeller.email}
                </span>
              </Alert>
            )}

            <FormGroup>
              <Label className="form-control-label">
                Nombre <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                name="first_name"
                className="form-control-alternative"
                value={formData.first_name}
                onChange={handleChange}
                placeholder="Nombre del vendedor"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label className="form-control-label">
                Apellido <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                name="last_name"
                className="form-control-alternative"
                value={formData.last_name}
                onChange={handleChange}
                placeholder="Apellido del vendedor"
                required
              />
            </FormGroup>

            <FormGroup className="mb-0">
              <Label className="form-control-label">
                Teléfono
              </Label>
              <Input
                type="tel"
                name="phone"
                className="form-control-alternative"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Ej: +52 123 456 7890"
              />
              <small className="text-muted">
                Teléfono de contacto del vendedor
              </small>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handleCloseModal}>
              <i className="ni ni-fat-remove me-1"></i>
              Cancelar
            </Button>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Guardando...
                </>
              ) : (
                <>
                  <i className="ni ni-check-bold me-1"></i>
                  Actualizar
                </>
              )}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
}

export default SellersManagement;
